import { Button } from "@nimit9/signet-ui/components/button"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@nimit9/signet-ui/components/combobox"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import {
  buildGroups,
  categoryHistory,
  groupDateLabel,
  type ReviewGroup,
  suggestedCategoryId,
} from "@paisa/finance/review"
import { defaultCategorySlug } from "@paisa/reconciliation"
import { type InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { z } from "zod"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useDateFormat } from "@/hooks/use-date-format"
import {
  INFINITE_TRANSACTIONS_KEY,
  type TransactionRow,
  transactionDisplayName,
  useInfiniteTransactions,
  useTransactions,
} from "@/hooks/use-transactions"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { pickableCategories } from "@/lib/pickable-categories"

const reviewSearchSchema = z.object({
  startDate: z.string().default("").catch(""),
  endDate: z.string().default("").catch(""),
})

export const Route = createFileRoute("/transactions/review")({
  validateSearch: reviewSearchSchema,
  component: ReviewPage,
})

function isoToday() {
  return new Date().toISOString().split("T")[0]
}

const CAT_TYPE_COLORS: Record<string, string> = {
  needs: "var(--cat-needs)",
  wants: "var(--cat-wants)",
  investments: "var(--cat-investments)",
  income: "var(--cat-income)",
  transfer: "var(--cat-transfer)",
}

// A bulk categorise is a money-row write across a whole group — give the
// person longer than react-hot-toast's 4s default to change their mind.
const BULK_UNDO_TOAST_MS = 10_000

function ReviewPage() {
  const { startDate: rawStart, endDate: rawEnd } = Route.useSearch()
  // No range given → every uncategorised row, so the count matches Home's
  // "N transactions to sort" (UAT J5-02). A link can still narrow it.
  const startDate = rawStart
  const endDate = rawEnd || isoToday()

  const qc = useQueryClient()
  const formatDate = useDateFormat()

  const { data: catData } = useCategories()
  const flatCats = catData ? flattenCategories(catData.data) : []
  const leafCats = pickableCategories(flatCats.filter((c) => !c.children?.length))

  // 200 is the API's hard cap (GET /transactions pageSize.max) — 500 here
  // silently 400'd every request (ZodError swallowed by useQuery's isError,
  // which this page didn't handle), so the deck always rendered "All caught
  // up" even with real uncategorised rows in the household. Found while
  // wiring the merchant-grouped review deck through a live backend (#PH3).
  // Pages accumulate (Load more / auto-advance below), so a household with
  // more than 200 uncategorised rows can still reach 100%.
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteTransactions({
    uncategorized: true,
    startDate,
    endDate,
    pageSize: 200,
  })

  // Offset pages can overlap when rows move in or out of the uncategorised
  // filter between page loads, so never let one row appear twice.
  const txns = useMemo(() => {
    const seen = new Set<string>()
    const out: TransactionRow[] = []
    for (const page of data?.pages ?? []) {
      for (const row of page.data) {
        if (seen.has(row.id)) continue
        seen.add(row.id)
        out.push(row)
      }
    }
    return out
  }, [data])
  // Live remaining count — refetched via a ["transactions"] prefix
  // invalidation after every categorise/undo (H2: this must never be used
  // as a fixed denominator, see initialTotal below).
  const firstPageTotal = data?.pages[0]?.total
  const currentTotal = firstPageTotal ?? 0

  // Locally hidden once a group is categorised — undo just clears the entry
  // for those ids and re-invalidates, so the group reappears from fresh data.
  const [categorised, setCategorised] = useState<Record<string, string>>({})
  // Per-group pending state: two groups categorised in quick succession each
  // keep their own disabled/dimmed state until their own request settles.
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set())
  function setGroupPending(key: string, pending: boolean) {
    setPendingKeys((prev) => {
      const next = new Set(prev)
      if (pending) next.add(key)
      else next.delete(key)
      return next
    })
  }

  // Seeded once from the first real total this session sees, then held
  // fixed for the whole review session — the denominator for "N of TOTAL
  // categorised". Deriving "done" from (initialTotal - currentTotal) instead
  // of accumulating local state means it can't desync into "4 of 3" / 133% /
  // a negative remaining count (H2).
  const [initialTotal, setInitialTotal] = useState<number | null>(null)
  useEffect(() => {
    if (initialTotal === null && firstPageTotal !== undefined) {
      setInitialTotal(firstPageTotal)
    }
  }, [firstPageTotal, initialTotal])

  const bulkMutation = useMutation({
    mutationFn: ({ ids, categoryId }: { ids: string[]; categoryId: string | null }) =>
      api.patch<{ success: boolean; updated: number }>("/transactions/bulk", { ids, categoryId }),
  })

  function invalidate() {
    // A write moves rows in/out of the uncategorised filter, which shifts
    // every later offset page: refetching the stale pages 2..N would skip or
    // repeat rows. Drop back to page 1 (it refills with what's left) and let
    // Load more / auto-advance fetch the rest fresh.
    qc.setQueriesData<InfiniteData<unknown, unknown>>(
      { queryKey: INFINITE_TRANSACTIONS_KEY },
      (old) =>
        old && old.pages.length > 1
          ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) }
          : old,
    )
    qc.invalidateQueries({ queryKey: ["transactions"] })
    qc.invalidateQueries({ queryKey: ["dashboard"] })
  }

  // mutateAsync, not mutate: TanStack only runs mutate()'s per-call callbacks
  // for the LATEST call, so two quick categorisations lost the first one's
  // success/undo handling. Each call awaits its own promise instead.
  async function undoGroup(ids: string[], groupLabel: string) {
    let res: { success: boolean; updated: number }
    try {
      res = await bulkMutation.mutateAsync({ ids, categoryId: null })
    } catch {
      // A failed undo previously did and showed nothing, leaving the user
      // thinking the undo worked when nothing changed server-side (M2).
      toast.error(`Couldn't undo — ${groupLabel} is still categorised. Try again.`)
      return
    }
    setCategorised((prev) => {
      const next = { ...prev }
      for (const id of ids) delete next[id]
      return next
    })
    invalidate()
    if (res.updated === ids.length) {
      toast(`Undone — ${groupLabel} back to uncategorised.`)
    } else {
      // Partial/failed undo — the user was about to be told it worked;
      // say clearly that it may not have (M2).
      toast.error(`Only partly undone — check ${groupLabel} before relying on this.`)
    }
  }

  // Synchronous per-group in-flight guard: `disabled` only lands on the next
  // render, so a rapid double selection could otherwise send two competing
  // bulk PATCHes for the same rows.
  const inFlight = useRef(new Set<string>())

  async function categoriseGroup(group: ReviewGroup, categoryId: string, categoryName: string) {
    if (inFlight.current.has(group.key)) return
    inFlight.current.add(group.key)
    const ids = group.txns.map((t) => t.id)
    setGroupPending(group.key, true)
    let res: { success: boolean; updated: number }
    try {
      res = await bulkMutation.mutateAsync({ ids, categoryId })
    } catch {
      toast.error(`Couldn't categorise ${group.label} — try again.`)
      return
    } finally {
      inFlight.current.delete(group.key)
      setGroupPending(group.key, false)
    }
    invalidate()
    if (res.updated !== ids.length) {
      // Partial write — don't claim success or locally hide rows that
      // may not actually have been updated; let the refetch above
      // settle which ones really moved (M2).
      toast.error(
        `Only ${res.updated} of ${ids.length} categorised as ${categoryName} — check and retry.`,
      )
      return
    }
    setCategorised((prev) => {
      const next = { ...prev }
      for (const id of ids) next[id] = categoryId
      return next
    })
    toast(
      (t) => (
        <div className="flex items-center gap-3">
          <span>
            {ids.length} categorised as {categoryName}
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              toast.dismiss(t.id)
              undoGroup(ids, group.label)
            }}
          >
            Undo
          </Button>
        </div>
      ),
      { duration: BULK_UNDO_TOAST_MS },
    )
  }

  const doneCount = initialTotal !== null ? Math.max(0, initialTotal - currentTotal) : 0
  const remaining = currentTotal
  const progress = initialTotal && initialTotal > 0 ? (doneCount / initialTotal) * 100 : 0

  const dateLabel = (() => {
    if (!rawStart && !rawEnd) return "All uncategorised"
    const s = startDate
      ? new Date(startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
      : ""
    const e = endDate
      ? new Date(endDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : ""
    return s && e ? `${s} – ${e}` : e || s
  })()

  const groups = useMemo(
    () =>
      buildGroups(
        txns.filter((t) => !categorised[t.id]),
        transactionDisplayName,
      ),
    [txns, categorised],
  )

  // Recent rows (any category) teach the suggestion what each payee usually is (UAT J5-03).
  const { data: recent } = useTransactions({ pageSize: 200 })
  const history = useMemo(() => categoryHistory(recent?.data ?? []), [recent])

  // Every loaded row categorised but more pages remain: advance on our own
  // rather than showing "All caught up" while rows are still waiting.
  const loadedAllHandled = !isLoading && groups.length === 0
  useEffect(() => {
    if (loadedAllHandled && hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [loadedAllHandled, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <>
      <Header
        title="Review"
        subtitle={
          isLoading
            ? undefined
            : remaining > 0
              ? `${remaining} uncategorised · ${dateLabel}`
              : dateLabel
        }
        showPeriod={false}
      />
      <PageWrapper>
        {/* Progress bar */}
        {initialTotal !== null && initialTotal > 0 && (
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] text-text-muted">
                {doneCount} of {initialTotal} categorised
              </span>
              <span
                className="text-[12px] font-semibold tabular-nums"
                style={{ color: "var(--accent-green)" }}
              >
                {Math.round(progress)}%
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-border-subtle">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, var(--accent-green), #34d399)",
                }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {isError && <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />}

        {/* All done */}
        {!isLoading && !isError && groups.length === 0 && !hasNextPage && !isFetchingNextPage && (
          <EmptyState
            title={doneCount > 0 ? "All caught up" : "Nothing to review"}
            description={
              doneCount > 0
                ? `You categorised ${doneCount} transaction${doneCount !== 1 ? "s" : ""}.`
                : "Nothing to sort right now. Rows from your next import will show up here."
            }
          />
        )}

        {/* Merchant-grouped review deck */}
        {groups.length > 0 && (
          <div className="space-y-2">
            {groups.map((group) => {
              const isPending = pendingKeys.has(group.key)
              const suggestedId = suggestedCategoryId(group, history, leafCats, defaultCategorySlug)
              const suggested = leafCats.find((c) => c.id === suggestedId)

              return (
                <div
                  key={group.key}
                  className={cn(
                    "flex flex-wrap items-center gap-3 rounded-[14px] border border-border-subtle bg-bg-surface px-4 py-3 sm:flex-nowrap",
                    isPending && "opacity-50",
                  )}
                >
                  {/* Avatar */}
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-bg-elevated text-sm font-medium text-text-secondary">
                    {group.label[0]?.toUpperCase() ?? "?"}
                  </div>

                  {/* Name + count + latest date */}
                  <div className="min-w-0 flex-1">
                    {/* Wraps on phone so the payee stays readable (UAT J5-01). */}
                    <p
                      data-testid="review-group-name"
                      className="line-clamp-2 break-words text-body font-medium text-text-primary sm:truncate"
                    >
                      {group.label}
                      {group.txns.length > 1 && (
                        <span className="ml-1.5 rounded-full bg-bg-elevated px-1.5 py-0.5 text-label font-semibold text-text-muted">
                          × {group.txns.length}
                        </span>
                      )}
                    </p>
                    <p className="text-label text-text-muted">
                      {groupDateLabel(group, formatDate)}
                    </p>
                  </div>

                  {/* Total amount */}
                  {/* Signed total: debits neutral with a minus, refunds/credits
                      carry a plus and the credit token (PAISA-AMT-02). */}
                  <Amount
                    value={group.total}
                    showSign
                    className="shrink-0 text-right text-body font-bold"
                  />

                  {/* One-tap suggestion, then the full picker (UAT J5-03) */}
                  {suggested && (
                    <Button
                      variant="outline"
                      size="touch"
                      className="sm:h-8"
                      disabled={isPending}
                      onClick={() => categoriseGroup(group, suggested.id, suggested.name)}
                      aria-label={`Categorise as ${suggested.name}`}
                    >
                      {suggested.icon} {suggested.name}
                    </Button>
                  )}

                  {/* Category picker — one tap categorises every row in the group */}
                  <Combobox
                    items={
                      suggested
                        ? [
                            suggested.id,
                            ...leafCats.filter((c) => c.id !== suggested.id).map((c) => c.id),
                          ]
                        : leafCats.map((c) => c.id)
                    }
                    itemToStringLabel={(id) => leafCats.find((c) => c.id === id)?.name ?? ""}
                    value={null}
                    onValueChange={(catId) => {
                      if (!catId) return
                      const cat = leafCats.find((c) => c.id === catId)
                      if (!cat) return
                      categoriseGroup(group, catId, cat.name)
                    }}
                  >
                    <ComboboxInput
                      placeholder="Categorise…"
                      className="h-11 w-full basis-full text-caption sm:h-8 sm:w-36 sm:basis-auto"
                      disabled={isPending}
                    />
                    <ComboboxContent className="w-56">
                      <ComboboxEmpty>No category found.</ComboboxEmpty>
                      <ComboboxList>
                        {(catId) => {
                          const cat = leafCats.find((c) => c.id === catId)
                          if (!cat) return null
                          return (
                            <ComboboxItem key={cat.id} value={cat.id}>
                              <span className="mr-1.5">{cat.icon}</span>
                              <span>{cat.name}</span>
                              <span
                                className="ml-auto text-[10px] capitalize"
                                style={{ color: CAT_TYPE_COLORS[cat.type] ?? "var(--text-muted)" }}
                              >
                                {cat.type}
                              </span>
                            </ComboboxItem>
                          )
                        }}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </div>
              )
            })}
          </div>
        )}

        {groups.length > 0 && hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="touch"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </PageWrapper>
    </>
  )
}
